import { Router } from '@lightningjs/sdk'

import Key from '../components/CustomKeyboard/Key'
import KeySpecial from '../components/CustomKeyboard/KeySpecial'
import {
  NavigationPointers,
  PointerOrientation,
} from '../components/NavigationPointers/NavigationPointers'
import TvCard from '../components/TvCards/TvBaseCard'
import TvTab from '../components/TvSearchLayout/TabMenu/TvTab'
import { SearchGridStates } from '../components/TvSearchLayout/TvSearchGrid/TvSearchGrid'
import TvSearchLayout, {
  layoutConfigType,
  SearchStates,
} from '../components/TvSearchLayout/TvSearchLayout'
import { getColor, grey960 } from '../constants/colors'
import { AppConfig } from '../services/appConfig'
import { TvCardTypesEnum } from '../types/flo-enums'
import { delayCall } from '../utils/misc'
import { genericError, networkError } from '../widgets/modal'
import PageHandler from './handlers/pageHandler'
export default class Search extends PageHandler {
  private _url: string
  private _lastLayoutConfig: layoutConfigType

  static _template() {
    return {
      w: 1920,
      h: 1080,
      rect: true,
      collision: true,
      color: getColor(grey960),
      flex: {},
      TvSearchLayout: {
        type: TvSearchLayout,
      },
      NavigationPointers: {
        type: NavigationPointers,
        pointerOrientation: PointerOrientation.VERTICAL,
      },
    }
  }

  get pageText() {
    return 'Search '
  }

  get tvSearchLayout() {
    return this.tag('TvSearchLayout') as TvSearchLayout
  }

  get navigationPointers() {
    return this.tag('NavigationPointers') as NavigationPointers
  }

  static _states() {
    return [
      class NotReady extends this {
        $enter() {
          this.setLoadingAnimation(true)
        }

        _handleKey() {
          return true
        }

        $exit() {
          this.setLoadingAnimation(false)
        }
      },
      class Ready extends this {
        $enter() {
          this.checkNavigationPointersVisibility()
          Router.focusPage()
        }

        _getFocused() {
          return this.tvSearchLayout
        }
      },
      class InBackgroundByModal extends this {
        _handleKey() {
          this.focusActiveModal()
        }
      },
    ]
  }

  get layoutConfig() {
    return this._lastLayoutConfig
  }

  set layoutConfig(config: layoutConfigType) {
    this._lastLayoutConfig = config
  }

  _onUrlParams({ url }: { url: string }) {
    this._url = url
  }

  async _onMounted() {
    this._isRoot = true

    this._setState('NotReady')

    await this.getInitialSearchData()
  }

  async getInitialSearchData() {
    // historyState() is called AFTER _onMounted().
    // This async call allows it to go first so we can
    // retrieve all the data before the process begins
    await delayCall()

    const url = this._lastLayoutConfig?.lastSearchUrl ?? this._url
    await this.tvSearchLayout.initView(url, this._lastLayoutConfig)
  }

  historyState(params) {
    if (params) {
      this._lastLayoutConfig = params.config
    } else {
      return {
        config: this.tvSearchLayout.lastLayoutConfig,
      }
    }
  }

  $connectionError(isNetworkError = false) {
    const error = isNetworkError ? networkError : genericError
    this.setModal(true, error)
  }

  $ready() {
    this._setState('Ready')
  }

  $upMovement() {
    this.tvSearchLayout?._setState(SearchStates.SearchGrid)
    this.tvSearchLayout?.searchGrid?._setState(SearchGridStates.Grid)
    this.tvSearchLayout?.searchGrid?._handleUp()
    this.checkNavigationPointersVisibility()
  }

  $downMovement() {
    this.tvSearchLayout?._setState(SearchStates.SearchGrid)
    this.tvSearchLayout?.searchGrid?._setState(SearchGridStates.Grid)
    this.tvSearchLayout?.searchGrid?._handleDown()
    this.checkNavigationPointersVisibility()
  }

  $nextPageLoadComplete() {
    this.checkNavigationPointersVisibility()
  }

  checkNavigationPointersVisibility() {
    const { isCursorVisible } = AppConfig.shared.appSettings
    const isFirstRowFocused = this.tvSearchLayout?.searchGrid?.isFirstRowFocused
    const isLastRowFocused = this.tvSearchLayout?.searchGrid?.isLastRowFocused
    const isGridEmpty = this.tvSearchLayout?.searchGrid?.isEmpty
    const isLoading = this.tvSearchLayout?.searchGrid?.loader.visible
    const isError = this.tvSearchLayout?.error?.visible

    // Special overrides to fit the search grid
    this.navigationPointers.pointerWidth = this.tvSearchLayout.rightContainer.w
    this.navigationPointers.paddingLeft =
      this.tvSearchLayout.leftContainer.w + this.tvSearchLayout.rightContainer.x
    this.navigationPointers.topContainer.y =
      this.tvSearchLayout?.searchInput?.finalH +
      this.tvSearchLayout?.searchGrid?.header?.finalH +
      15
    this.navigationPointers.topContainer.h = 100
    this.navigationPointers.renderGradient()

    this.adjustNavigationPointersCollisionBox()

    const showTopPointer = !isFirstRowFocused && !isGridEmpty && !isLoading && !isError
    const showBottomPointer = !isLastRowFocused && !isGridEmpty && !isLoading && !isError

    this.navigationPointers.visible = isCursorVisible
    this.navigationPointers.topContainer.collision = showTopPointer && isCursorVisible
    this.navigationPointers.showTopPointer = showTopPointer && isCursorVisible
    this.navigationPointers.showBottomPointer = showBottomPointer && isCursorVisible

    this._refocus()
  }

  _handleScroll({ deltaY }) {
    const isGoingDown = deltaY > 0
    const isGoingUp = !isGoingDown
    const isGridEmpty = this.tvSearchLayout?.searchGrid?.isEmpty
    const isLoading = this.tvSearchLayout?.searchGrid?.loader.visible
    const isError = this.tvSearchLayout?.error?.visible

    if (isGridEmpty || isLoading || isError) {
      return
    }

    this.tvSearchLayout?._setState(SearchStates.SearchGrid)
    this.tvSearchLayout?.searchGrid?._setState(SearchGridStates.Grid)

    const isFirstRowFocused = this.tvSearchLayout?.searchGrid?.isFirstRowFocused
    if (!(isFirstRowFocused && isGoingUp)) {
      this.tvSearchLayout?.searchGrid?.handleScroll({ deltaY })
    }

    this.checkNavigationPointersVisibility()
  }

  // Increases the height of elements that overflow the navigation pointers that prevent
  // the user from clicking the tiles, even when not being fully covered by the gradients,
  // depending onf the type of card being shown
  adjustNavigationPointersCollisionBox() {
    const currentRow = this.tvSearchLayout?.searchGrid?.currentRow
    const currentRowType = currentRow?.items?.[0]?.data?.type
    const isFirstRowFocused = this.tvSearchLayout?.searchGrid?.isFirstRowFocused
    const isLastRowFocused = this.tvSearchLayout?.searchGrid?.isLastRowFocused
    const isFocusInTheMiddle = !isFirstRowFocused && !isLastRowFocused

    if (
      isFirstRowFocused &&
      (currentRowType === TvCardTypesEnum.TvEventCard ||
        currentRowType === TvCardTypesEnum.TvVideoCard)
    ) {
      this.navigationPointers.topOverflowCollisionBox.h = 0
      this.navigationPointers.bottomOverflowCollisionBox.h = 230
    } else if (isFirstRowFocused && currentRowType === TvCardTypesEnum.TvEntityCard) {
      this.navigationPointers.topOverflowCollisionBox.h = 0
      this.navigationPointers.bottomOverflowCollisionBox.h = 80
    } else if (
      isFocusInTheMiddle &&
      (currentRowType === TvCardTypesEnum.TvEventCard ||
        currentRowType === TvCardTypesEnum.TvVideoCard)
    ) {
      this.navigationPointers.topOverflowCollisionBox.h = 60
      this.navigationPointers.bottomOverflowCollisionBox.h = 90
    } else if (isFocusInTheMiddle && currentRowType === TvCardTypesEnum.TvEntityCard) {
      this.navigationPointers.topOverflowCollisionBox.h = 0
      this.navigationPointers.bottomOverflowCollisionBox.h = 30
    } else if (
      isLastRowFocused &&
      (currentRowType === TvCardTypesEnum.TvEventCard ||
        currentRowType === TvCardTypesEnum.TvVideoCard)
    ) {
      this.navigationPointers.topOverflowCollisionBox.h = 280
      this.navigationPointers.bottomOverflowCollisionBox.h = 0
    } else if (isLastRowFocused && currentRowType === TvCardTypesEnum.TvEntityCard) {
      this.navigationPointers.topOverflowCollisionBox.h = 130
      this.navigationPointers.bottomOverflowCollisionBox.h = 0
    } else {
      this.navigationPointers.topOverflowCollisionBox.h = 0
      this.navigationPointers.bottomOverflowCollisionBox.h = 0
    }
  }

  $updateFocusByHover(element) {
    if (element === this.tvSearchLayout?.actionButton) {
      this.tvSearchLayout?._setState(SearchStates.ActionButton)
    } else if (element instanceof Key || element instanceof KeySpecial) {
      this.tvSearchLayout?._setState(SearchStates.Keyboard)

      const { rowIndex, index } = element
      this.tvSearchLayout.keyboard.index = rowIndex
      this.tvSearchLayout.keyboard.currentRow.keyIndex = index
    } else if (element instanceof TvTab) {
      this.tvSearchLayout?._setState(SearchStates.SearchGrid)
      this.tvSearchLayout?.searchGrid?._setState(SearchGridStates.Tabs)
    } else if (element instanceof TvCard) {
      this.tvSearchLayout?._setState(SearchStates.SearchGrid)
      this.tvSearchLayout?.searchGrid?._setState(SearchGridStates.Grid)

      const { rowIndex, cardIndex } = element
      this.tvSearchLayout.searchGrid.currentRowIndex = rowIndex
      this.tvSearchLayout.searchGrid.currentCardIndex = cardIndex
    }

    this._refocus()
  }

  _onCursorEnter() {
    this.checkNavigationPointersVisibility()
  }

  _onCursorLeave() {
    this.checkNavigationPointersVisibility()

    if (this.tvSearchLayout?.state === SearchStates.SearchGrid) {
      if (this.tvSearchLayout?.searchGrid?.state === SearchGridStates.Grid) {
        this.tvSearchLayout?.searchGrid?.currentRow?.currentCard?._focus()
      } else if (this.tvSearchLayout?.searchGrid?.state === SearchGridStates.Tabs) {
        this.tvSearchLayout?.searchGrid?.tabMenu?.currentTab?._focus()
      }
    } else if (this.tvSearchLayout?.state === SearchStates.Keyboard) {
      this.tvSearchLayout?.keyboard?.currentRow?.currentKey?._focus()
    } else if (this.tvSearchLayout?.state === SearchStates.ActionButton) {
      this.tvSearchLayout?.actionButton?._focus()
    }
  }
}
